import React, { useEffect, useState, useRef } from 'react';
import { LEGEND_COLOR_MAP_NEW } from '../../constants';
import { PieWrapper } from './style';
import FullPieChart from './d3Pie-fullPie';
import HalfPieChart from './d3Pie-halfPie';

const PieAndDonutParent = ({ 
  id,
  data, 
  settings, 
  halfPieSettings,
  titleOrderedArrayFunc,
  sortedPieColorObject
}) => {
  const [mainData, setMainData] = useState([]);
  const [breakdownData, setBreakdownData] = useState([]);
  const [totalPct, setTotalPct] = useState("0%");
  const halfPieWidth = 350;
  const halfPieSettingsNew = halfPieSettings

  // Use a ref to track what we've already sent to the parent
  // This prevents the "Maximum update depth exceeded" error
  const lastSentTitlesRef = useRef("");

  useEffect(() => {
    if (!data || data.length === 0) return;

    // 1. Clean and initial sort by value
    const cleaned = data.map(d => ({
      ...d,
      value: typeof d.value === 'string' ? parseFloat(d.value.replace(/[^\d.]/g, '')) : (d.value || 0)
    })).sort((a, b) => b.value - a.value);

    const totalSum = cleaned.reduce((sum, d) => sum + d.value, 0);
    
    // 2. Logic for small datasets (Single Pie)
    if (cleaned.length <= 13) {
      const sortedSingle = [...cleaned].sort((a, b) => {
        if (a.name === 'OTHERS') return 1;
        if (b.name === 'OTHERS') return -1;
        if (a.name === 'REMAINING_VALUE') return 1;
        if (b.name === 'REMAINING_VALUE') return -1;
        return 0;
      });

      setMainData(sortedSingle);
      setBreakdownData([]);
      setTotalPct("100%");
      return;
    }

    // 3. Logic for large datasets (Pie + Half Pie)
    let currentSum = 0;
    let tempMain = [];
    let tempBreakdown = [];

    cleaned.forEach((item) => {
      const itemPercentage = (item.value / totalSum) * 100;
      const currentRunningPercentage = (currentSum / totalSum) * 100;

      const isOverLimit = tempMain.length >= 12;
      const isLowValue = item.value < 4;
      const isLowPercentage = itemPercentage < 5;
      const isOverThreshold = currentRunningPercentage >= 78;
      const isRemainingValue = item.name === 'REMAINING_VALUE';

      if(isRemainingValue){
        tempMain.push(item);
        currentSum += item.value;
      } else if (isOverLimit || isLowValue || isLowPercentage || isOverThreshold) {
        tempBreakdown.push(item);
      } else {
        tempMain.push(item);
        currentSum += item.value;
      }
    });

    // 4. Balancing logic
    if (tempBreakdown.length > 15) {
      const moveBackCount = tempBreakdown.length - 15;
      const toMoveBack = tempBreakdown.splice(0, moveBackCount);
      tempMain = [...tempMain, ...toMoveBack];
      currentSum = tempMain.reduce((sum, d) => sum + d.value, 0);
    }

    // 5. Final positioning (REMAINING_VALUE and OTHERS at the end)
    const standardItems = tempMain.filter(item => item.name !== 'REMAINING_VALUE');
    const remainingValueItem = tempMain.find(item => item.name === 'REMAINING_VALUE');

    const getColorsForData = (d1, slideLen) => {
      const d2 = d1.slice(slideLen)
      return d2;
    }

    if (tempBreakdown.length > 0) {
      const othersSum = tempBreakdown.reduce((sum, d) => sum + d.value, 0);
      
      const finalMain = [
        ...standardItems,
        ...(remainingValueItem ? [remainingValueItem] : []), 
        { name: 'OTHERS', value: othersSum }
      ];

      setMainData(finalMain);
      setBreakdownData(tempBreakdown);

      halfPieSettingsNew.color = getColorsForData(halfPieSettings.color, finalMain.length);
      halfPieSettingsNew.colors = getColorsForData(halfPieSettings.colors, finalMain.length);
      halfPieSettingsNew.textColors = getColorsForData(halfPieSettings.textColors, finalMain.length);

      const mainPercentage = ((currentSum / totalSum) * 100).toFixed(0);
      setTotalPct(`${mainPercentage}%`);
    } else {
      const finalMain = [
        ...standardItems,
        ...(remainingValueItem ? [remainingValueItem] : [])
      ];
      setMainData(finalMain);
      setBreakdownData([]);
      setTotalPct("100%");
    }
  }, [data]);

  /**
   * FIXED EFFECT: 
   * We compare the current title list against the ref.
   * Only if the actual text content changes do we call titleOrderedArrayFunc.
   */
  useEffect(() => {
    if (mainData.length === 0) return;

    const titlesInOrderArray = [...mainData, ...breakdownData].map(d => d.name);
    const titlesString = JSON.stringify(titlesInOrderArray);

    if (lastSentTitlesRef.current !== titlesString) {
      lastSentTitlesRef.current = titlesString;
      
      // FIX: Added a check to ensure it's a function before calling it
      if (typeof titleOrderedArrayFunc === 'function') {
        titleOrderedArrayFunc(titlesInOrderArray);
      }
    }
  }, [mainData, breakdownData, titleOrderedArrayFunc]);

  const renderSingle = () => (
    React.createElement('div', { style: { display: 'flex', justifyContent: 'center', width: '100%' } },
      React.createElement(FullPieChart, { 
        data: mainData, 
        settings: {...settings, height: 400}, 
        centerText: totalPct,
        id: id,
        color: LEGEND_COLOR_MAP_NEW,
        pieType: "single",
        sortedPieColorObject: sortedPieColorObject
      })
    )
  );

  const renderPaired = () => (
    React.createElement(PieWrapper, { style: { display: 'flex', alignItems: 'center', width: '100%', paddingLeft: '20px' } },
      React.createElement('div', { style: { width: `calc( 100% - ${halfPieWidth}px )`, maxWidth:'800px' } },
        React.createElement(FullPieChart, { 
          data: mainData, 
          settings: settings, 
          centerText: totalPct,
          id: id,
          color: LEGEND_COLOR_MAP_NEW,
          pieType: "both",
          sortedPieColorObject: sortedPieColorObject
        })
      ),
      React.createElement('div', { style: { width: `${halfPieWidth}px` } },
        React.createElement(HalfPieChart, { 
          data: breakdownData, 
          settings: halfPieSettingsNew || settings, 
          centerText: `${(100 - parseFloat(totalPct)).toFixed(0)}%`,
          id: id,
          color: LEGEND_COLOR_MAP_NEW,
          // color: Object.fromEntries(Object.entries(LEGEND_COLOR_MAP_NEW).slice(mainData.length)),
          halfPieWidth: halfPieWidth,
          sortedPieColorObject: sortedPieColorObject
        })
      )
    )
  );

  return React.createElement('div', { style: { width: '100%' } },
    breakdownData.length === 0 ? renderSingle() : renderPaired()
  );
};

export default PieAndDonutParent;